import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;

export interface FeaturesConfig {
  templates: boolean;
  connectors: boolean;
  settingsWorkspace: boolean;
  settingsNotifications: boolean;
  settingsIntegration: boolean;
  createWorkspace: boolean;
  mediaLibrary: boolean;
  mediaLibraryProviders: string[];
  agentEngine: 'claude' | 'opencode';
  byoc: boolean;
  optisignsAssets: boolean;
  ownershipTransfer: boolean;
  broadcast: boolean;
  codexEngine: boolean;
}

export interface PlanFeaturesConfig {
  models: string[];
  visibility: string[];
  customDomain: boolean;
}

export interface AllPlanFeaturesConfig {
  free: PlanFeaturesConfig;
  standard: PlanFeaturesConfig;
  pro: PlanFeaturesConfig;
  enterprise?: PlanFeaturesConfig;
  embed?: PlanFeaturesConfig;
  /** Embed workspaces on trial (subscription.status === 'trialing'). Restricted (no opus). */
  embed_trial?: PlanFeaturesConfig;
  /** Embed workspaces with paid subscription (subscription.status === 'active'). Full access. */
  embed_active?: PlanFeaturesConfig;
}

export interface NetworkConfig {
  egressIps: string[]; // Static IPs for database whitelist
}

// ─── Embed onboarding (industry-personalized welcome page) ───

export interface EmbedChipConfig {
  label: string;
  icon: string; // Lucide icon name, resolved via the curated industry-icons map
  prompt: string;
}

export interface EmbedDemoConfig {
  label: string;
  description: string;
  /** Optional deployed app — present → live iframe preview; absent → prompt idea. */
  deployUrl?: string;
  thumbnailUrl?: string;
  promptSeed?: string;
}

export interface EmbedIndustryContent {
  displayName?: string;
  chips?: EmbedChipConfig[];
  demos?: EmbedDemoConfig[];
  /** Meet-modal demo to feature first: sales | storefront | allhands | patient. */
  featuredUseCase?: string;
}

export interface EmbedOnboardingConfig {
  default: EmbedIndustryContent;
  industries: Record<string, EmbedIndustryContent>;
  aliases: Record<string, string>;
}

export const featuresApi = {
  /**
   * Get public feature flags
   * No authentication required
   */
  async getFeatures(): Promise<FeaturesConfig> {
    const response = await axios.get(`${API_URL}/api/public/features`);
    return response.data;
  },

  /**
   * Get plan-based feature restrictions
   * No authentication required - used for paywall UI
   */
  async getPlanFeatures(): Promise<AllPlanFeaturesConfig> {
    const response = await axios.get(`${API_URL}/api/public/plan-features`);
    return response.data;
  },

  /**
   * Get network configuration (egress IPs for database whitelist)
   * No authentication required - used by connectors UI
   */
  async getNetworkConfig(): Promise<NetworkConfig> {
    const response = await axios.get(`${API_URL}/api/public/network`);
    return response.data;
  },

  /**
   * Get industry-personalized embed onboarding content.
   * No authentication required - consumed by the embed welcome page.
   */
  async getEmbedOnboarding(): Promise<EmbedOnboardingConfig> {
    const response = await axios.get(`${API_URL}/api/public/embed-onboarding`);
    return response.data;
  },
};
